'use client';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import Image from 'next/image';
import UpdatedIcon from '@/public/updated.png';

const HiringModels = ({ title, description, models }) => {
  return (
    <section className="w-full px-4">
      <div className="max-w-7xl mx-auto">
        {/* Title Section */}
        <div className="text-center mb-16">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-normal leading-tight tracking-tight pb-1.5"
            style={{
              background: `linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.9) 100%,
                rgba(255, 255, 255, 0.7) 70%,
                rgba(255, 255, 255, 0.4) 30%,
                rgba(255, 255, 255, 0.2) 20%
              )`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              textShadow: '0px 2px 3px rgba(0,0,0,0.3)',
            }}
          >
            {title || 'Hiring Models'}
          </h1>
          <div
            className="mt-6 text-gray-500 text-lg md:text-xl max-w-3xl mx-auto"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {models?.map((model, index) => (
            <FeatureCard
              key={model?.title || index}
              title={model?.title}
              description={model?.description}
              icon={model?.icon}
              list={model?.points || []}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ title, description, icon, list }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <div
      ref={ref}
      className={`relative min-h-[500px] p-8 rounded-2xl bg-[#0C1015]
        overflow-hidden
        ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} 
        transition-all duration-700 ease-out group
        hover:border-[#1FBCCB]/40 border-2 border-transparent
        hover:shadow-[0_0_15px_rgba(31,188,203,0.15)]
        `}
      style={{
        background:
          'linear-gradient(180deg, rgba(31, 188, 203, 0.1) 0%, rgba(2, 12, 13, 0) 30%, rgba(2, 12, 13, 0) 70%, rgba(31, 188, 203, 0.1) 100%)',
      }}
    >
      {/* Glowing border overlay */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-95 transition-opacity duration-300"
        style={{
          background:
            'linear-gradient(180deg, rgba(31, 188, 203, 0.2) 0%, rgba(31, 188, 203, 0.1) 30%, rgba(31, 188, 203, 0.1) 70%, rgba(31, 188, 203, 0.2) 100%)',
          margin: '-1px',
          pointerEvents: 'none',
        }}
      />

      {/* Top Glow */}
      <div
        className="absolute top-0 left-0 right-0 h-[30%]"
        style={{
          background:
            'linear-gradient(180deg, rgba(31, 188, 203, 0.15) 0%, transparent 100%)',
        }}
      />

      {/* Bottom Glow */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[30%]"
        style={{
          background:
            'linear-gradient(0deg, rgba(31, 188, 203, 0.15) 0%, transparent 100%)',
        }}
      />

      {/* Card content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          {icon ? (
            <div
              dangerouslySetInnerHTML={{ __html: icon }}
              className="w-[40px] h-[40px] text-white group-hover:scale-110 transition-transform duration-300 brightness-200"
            />
          ) : (
            <Image
              src={UpdatedIcon}
              alt="Default Icon"
              width={40}
              height={40}
              className="text-white group-hover:scale-110 transition-transform duration-300 brightness-200 object-contain"
            />
          )}
        </div>

        {/* Title */}
        <h3 className="text-white text-xl text-center mb-4 group-hover:text-[#1FBCCB] transition-colors duration-300">
          {title}
        </h3>

        {/* Description */}
        <p className="text-sm leading-relaxed mb-8 text-center text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
          {description}
        </p>

        {/* Separator Line */}
        <div className="w-full h-px bg-[#1FBCCB]/10 my-6 group-hover:bg-[#1FBCCB]/20 transition-colors duration-300" />

        {/* What you will get section */}
        <div className="mt-auto">
          <h3 className="text-white text-lg mb-4 group-hover:text-[#1FBCCB] transition-colors duration-300">
            What you will get
          </h3>
          <ul className="space-y-3 pl-10">
            {list?.map((item, index) => (
              <li
                key={index}
                className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-300 list-disc"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HiringModels;